/**
 * `PlansTabs` — "Plans" | "Add-ons" tab strip at the top of the plan
 * catalog surface (ADR-0263 S1).
 *
 * Lives inside the Plans view family instead of a sibling top-level
 * nav entry because the left menu is already crowded (founder rule
 * 2026-10-10 — see `feedback_agency_portal_nav`). Same visual
 * language as `AccountDetailTabs`: thin bottom-border strip with the
 * active tab highlighted in text + a lime underline.
 */

import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";

export type PlansTabId = 'plans' | 'addons' | 'pricing-page';

export interface PlansTabsProps {
  value: PlansTabId;
  onChange: (id: PlansTabId) => void;
}

const TABS: ReadonlyArray<{ id: PlansTabId; label: string }> = [
  { id: 'plans', label: 'Plans' },
  { id: 'addons', label: 'Add-ons' },
  { id: 'pricing-page', label: 'Pricing page' },
];

export function PlansTabs({ value, onChange }: PlansTabsProps): JSX.Element {
  const C = useC();
  return (
    <div
      role="tablist"
      aria-label="Plan catalog sections"
      style={{
        display: 'flex',
        gap: 4,
        borderBottom: `1px solid ${C.border}`,
        marginBottom: 18,
        overflowX: 'auto',
      }}
    >
      {TABS.map((t) => {
        const active = t.id === value;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.id)}
            style={{
              background: 'transparent',
              border: 0,
              padding: '10px 14px',
              borderBottom: `2px solid ${active ? C.limeFg : 'transparent'}`,
              marginBottom: -1,
              color: active ? C.text : C.muted,
              fontFamily: sansFont,
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              cursor: 'pointer',
              minHeight: 44,
              whiteSpace: 'nowrap',
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
